import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { PROJECT_CSV_HEADERS } from '@domain/constants/csv-headers';
import { CsvDataHelper } from '@domain/interface/data-map';
import {
	DIFFERENCE_IN_DAYS,
	IS_NOT_EMPTY,
	IS_NULL_OR_UNDEFINED,
	IS_VALID_DATE,
} from 'cargo-chief-commons';
import {
	ACCEPTED_DATE_FORMATS,
	IS_EMPTY_LINE,
	IS_VALID_LINE,
	MAP_HEADERS,
	MAP_INDICES,
} from '../shared/utils';
import { environment } from '@environment/environment';

export const PROJECT_DATA_MAP = (formBuilder: UntypedFormBuilder): CsvDataHelper => ({
	headersMap: [
		{ value: PROJECT_CSV_HEADERS.ORIGIN_CITY, label: 'Origin City', required: true },
		{ value: PROJECT_CSV_HEADERS.ORIGIN_STATE, label: 'Origin State', required: true },
		{ value: PROJECT_CSV_HEADERS.ORIGIN_ZIP, label: 'Origin Zip', required: false },
		{ value: PROJECT_CSV_HEADERS.DESTINATION_CITY, label: 'Destination City', required: true },
		{ value: PROJECT_CSV_HEADERS.DESTINATION_STATE, label: 'Destination State', required: true },
		{ value: PROJECT_CSV_HEADERS.DESTINATION_ZIP, label: 'Destination Zip', required: false },
		{ value: PROJECT_CSV_HEADERS.TRAILER_TYPE, label: 'Trailer Type', required: true },
		{ value: PROJECT_CSV_HEADERS.DUE_DATE, label: 'Due Date', required: false },
		{ value: PROJECT_CSV_HEADERS.VALUE, label: 'Value', required: false },
		{ value: PROJECT_CSV_HEADERS.MAX_BUY, label: 'Max Buy', required: false },
		{ value: PROJECT_CSV_HEADERS.CUSTOMER_RATE, label: 'Customer Rate', required: false },
		{ value: PROJECT_CSV_HEADERS.COMMODITY, label: 'Commodity', required: false },
		{ value: PROJECT_CSV_HEADERS.TOTAL_WEIGHT, label: 'Total Weight', required: false },
		{ value: PROJECT_CSV_HEADERS.DURATION, label: 'Duration', required: true },
		{ value: PROJECT_CSV_HEADERS.COUNT, label: 'Count', required: true },
		{ value: PROJECT_CSV_HEADERS.MONDAY, label: 'Monday', required: false },
		{ value: PROJECT_CSV_HEADERS.TUESDAY, label: 'Tuesday', required: false },
		{ value: PROJECT_CSV_HEADERS.WEDNESDAY, label: 'Wednesday', required: false },
		{ value: PROJECT_CSV_HEADERS.THURSDAY, label: 'Thursday', required: false },
		{ value: PROJECT_CSV_HEADERS.FRIDAY, label: 'Friday', required: false },
		{ value: PROJECT_CSV_HEADERS.SATURDAY, label: 'Saturday', required: false },
		{ value: PROJECT_CSV_HEADERS.SUNDAY, label: 'Sunday', required: false },
		{ value: PROJECT_CSV_HEADERS.PACKAGING, label: 'Packaging', required: false },
		{
			value: PROJECT_CSV_HEADERS.TRAILER_REQUIREMENTS,
			label: 'Trailer Requirements',
			required: false,
		},
		{ value: PROJECT_CSV_HEADERS.SHIPPER_NOTES, label: 'Shipper Notes', required: false },
		{ value: PROJECT_CSV_HEADERS.RECEIVER_NOTES, label: 'Receiver Notes', required: false },
		{ value: PROJECT_CSV_HEADERS.CUSTOMER_NAME, label: 'Customer Name', required: false },
		{ value: PROJECT_CSV_HEADERS.LAST_BID, label: 'Last Bid', required: false },
	],
	requiredHeaders: [
		PROJECT_CSV_HEADERS.ORIGIN_CITY,
		PROJECT_CSV_HEADERS.ORIGIN_STATE,
		PROJECT_CSV_HEADERS.DESTINATION_CITY,
		PROJECT_CSV_HEADERS.DESTINATION_STATE,
		PROJECT_CSV_HEADERS.TRAILER_TYPE,
	],
	fileDataValidator: (
		fileHeaders: string,
		fileData: string[],
		requiredHeaders: string[]
	): string[] => {
		let errors = [];
		let headers = fileHeaders.split(',');
		let headerMap = MAP_HEADERS(headers);
		let indicesMap = MAP_INDICES(headers);

		if (fileData.length > 0) {
			for (var i = 0; i < fileData.length; i++) {
				var line = fileData[i];
				if (IS_EMPTY_LINE(line)) {
					continue;
				}
				var rowElements = line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
				if (rowElements.length != headers.length) {
					errors.push(
						`Number of columns required ${headers.length}. Number of columns provided in csv ${rowElements.length}`
					);
					return errors;
				}
				if (IS_VALID_LINE(line, indicesMap, requiredHeaders)) {
					var dueDateValue = rowElements[headerMap[PROJECT_CSV_HEADERS.DUE_DATE]];
					if (!IS_VALID_DATE(dueDateValue, ACCEPTED_DATE_FORMATS)) {
						errors.push(
							'Invalid date format for Due Date: ' +
								dueDateValue +
								'. Accepted formats: M/D/Y, M-D-Y, Y-M-D'
						);
					}
					if (DIFFERENCE_IN_DAYS(new Date(), dueDateValue) < 1) {
						errors.push('Due Date cannot be less than current day.');
					}
				} else {
					const originCity = rowElements[headerMap[PROJECT_CSV_HEADERS.ORIGIN_CITY]],
						originState = rowElements[headerMap[PROJECT_CSV_HEADERS.ORIGIN_STATE]],
						destinationCity = rowElements[headerMap[PROJECT_CSV_HEADERS.DESTINATION_CITY]],
						destinationState = rowElements[headerMap[PROJECT_CSV_HEADERS.DESTINATION_STATE]],
						trailerType = rowElements[headerMap[PROJECT_CSV_HEADERS.TRAILER_TYPE]];

					if (IS_NULL_OR_UNDEFINED(originCity) || !IS_NOT_EMPTY(originCity)) {
						errors.push('Origin City is required.');
					}
					if (IS_NULL_OR_UNDEFINED(originState) || !IS_NOT_EMPTY(originState)) {
						errors.push('Origin State is required.');
					}
					if (IS_NULL_OR_UNDEFINED(destinationCity) || !IS_NOT_EMPTY(destinationCity)) {
						errors.push('Destination City is required.');
					}
					if (IS_NULL_OR_UNDEFINED(destinationState) || !IS_NOT_EMPTY(destinationState)) {
						errors.push('Destination State is required.');
					}
					if (IS_NULL_OR_UNDEFINED(trailerType) || !IS_NOT_EMPTY(trailerType)) {
						errors.push('Trailer Type is required.');
					}
					break;
				}
			}
		} else {
			errors.push('No record found.');
		}

		return errors;
	},

	formGroupBuilder: (): UntypedFormGroup => {
		let form: UntypedFormGroup;
		form = formBuilder.group({
			[PROJECT_CSV_HEADERS.ORIGIN_CITY]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.ORIGIN_STATE]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.ORIGIN_ZIP]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.DESTINATION_CITY]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.DESTINATION_STATE]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.DESTINATION_ZIP]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.TRAILER_TYPE]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.DUE_DATE]: ['', Validators.required],
			[PROJECT_CSV_HEADERS.DUE_DATE]: [''],
			[PROJECT_CSV_HEADERS.VALUE]: [''],
			[PROJECT_CSV_HEADERS.MAX_BUY]: [''],
			[PROJECT_CSV_HEADERS.CUSTOMER_RATE]: [''],
			[PROJECT_CSV_HEADERS.COMMODITY]: [''],
			[PROJECT_CSV_HEADERS.TOTAL_WEIGHT]: [''],
			[PROJECT_CSV_HEADERS.DURATION]: [''],
			[PROJECT_CSV_HEADERS.COUNT]: [''],
			[PROJECT_CSV_HEADERS.MONDAY]: [false],
			[PROJECT_CSV_HEADERS.TUESDAY]: [false],
			[PROJECT_CSV_HEADERS.WEDNESDAY]: [false],
			[PROJECT_CSV_HEADERS.THURSDAY]: [false],
			[PROJECT_CSV_HEADERS.FRIDAY]: [false],
			[PROJECT_CSV_HEADERS.SATURDAY]: [false],
			[PROJECT_CSV_HEADERS.SUNDAY]: [false],
			[PROJECT_CSV_HEADERS.PACKAGING]: [''],
			[PROJECT_CSV_HEADERS.TRAILER_REQUIREMENTS]: [''],
			[PROJECT_CSV_HEADERS.SHIPPER_NOTES]: [''],
			[PROJECT_CSV_HEADERS.RECEIVER_NOTES]: [''],
			[PROJECT_CSV_HEADERS.CUSTOMER_NAME]: [''],
			[PROJECT_CSV_HEADERS.LAST_BID]: [''],
		});
		return form;
	},

	fileUploadBucketName: environment.BULK_MATCH_CONFIG.project.bucketName,
});
